import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Target, TrendingUp, ChevronRight, Loader2, Crosshair, Activity } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';

const API_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http:\/\//i, 'https://') + '/api';

const SPORT_EMOJI = {
  'Soccer': 'circle', 'Basketball': 'circle', 'Ice Hockey': 'circle',
  'American Football': 'circle', 'Baseball': 'circle', 'Rugby': 'circle',
  'Cricket': 'circle', 'Tennis': 'circle', 'Motorsport': 'circle',
  'Handball': 'circle', 'Volleyball': 'circle', 'ESports': 'circle',
};

const MultiSportPage = () => {
  const navigate = useNavigate();
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [multiTips, setMultiTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [predLoading, setPredLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, tRes] = await Promise.all([
          fetch(`${API_URL}/predictions/sports`).then(r => r.json()),
          fetch(`${API_URL}/predictions/multi-sport-tips?limit=8`).then(r => r.json()),
        ]);
        setSports(sRes.sports || []);
        setMultiTips(tRes.tips || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const loadPredictions = async (sport) => {
    setSelectedSport(sport);
    setPredLoading(true);
    try {
      const res = await fetch(`${API_URL}/predictions/predict/${sport}?limit=10`);
      const data = await res.json();
      setPredictions(data);
    } catch (e) { console.error(e); }
    setPredLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#00BFA6] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#121212] pb-24">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => selectedSport ? (setSelectedSport(null), setPredictions(null)) : navigate('/dashboard')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Globe className="w-6 h-6 text-[#00BFA6]" />
          <h1 className="text-xl font-bold text-white">{selectedSport || 'Multi-Sport Predictions'}</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {!selectedSport ? (
          <>
            {/* Multi-Sport Top Tips */}
            {multiTips.length > 0 && (
              <div data-testid="multi-sport-tips" className="mb-6">
                <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Crosshair className="w-4 h-4 text-[#00BFA6]" />
                  Top Tipps ueber alle Sportarten
                </h2>
                <div className="space-y-2">
                  {multiTips.map((tip, i) => (
                    <div key={i} className="p-3 bg-[#1E1E1E] rounded-xl border border-white/5 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                        tip.confidence >= 70 ? 'bg-[#00BFA6]/20 text-[#00BFA6]' :
                        tip.confidence >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {tip.confidence}%
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{tip.home_team} vs {tip.away_team}</div>
                        <div className="text-[10px] text-gray-500">{tip.sport} | {tip.league}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${
                          tip.prediction === 'home' ? 'text-blue-400' : tip.prediction === 'away' ? 'text-red-400' : 'text-gray-400'
                        }`}>
                          {tip.prediction === 'home' ? 'Heim' : tip.prediction === 'away' ? 'Ausw.' : 'X'}
                        </div>
                        {tip.is_correct !== undefined && (
                          <div className={`text-[9px] ${tip.is_correct ? 'text-[#00BFA6]' : 'text-red-400'}`}>
                            {tip.is_correct ? 'Richtig' : 'Falsch'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sport Grid */}
            <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#7B1FA2]" />
              Sportarten ({sports.length})
            </h2>
            <div data-testid="sports-grid" className="grid grid-cols-2 gap-2">
              {sports.map((s, i) => (
                <button
                  key={i}
                  data-testid={`sport-card-${s.sport}`}
                  onClick={() => loadPredictions(s.sport)}
                  className="p-4 bg-[#1E1E1E] rounded-xl border border-white/5 hover:border-[#00BFA6]/30 transition-all text-left"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{s.sport}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
                  </div>
                  <div className="flex gap-3 text-[10px] text-gray-500">
                    <span>{(s.matches || 0).toLocaleString()} Matches</span>
                    <span>{s.leagues} Ligen</span>
                  </div>
                  {s.accuracy?.precision > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      <div className="h-1 flex-1 bg-white/5 rounded-full">
                        <div className="h-1 rounded-full bg-[#00BFA6]" style={{ width: `${Math.min(s.accuracy.precision, 100)}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-[#00BFA6]">{s.accuracy.precision}%</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        ) : (
          /* Sport Predictions View */
          <div data-testid="sport-predictions">
            {predLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#00BFA6] animate-spin" />
              </div>
            ) : predictions ? (
              <>
                <div className="flex items-center gap-3 mb-4 p-3 bg-[#1E1E1E] rounded-xl border border-white/5">
                  <div className="text-2xl font-black font-mono text-[#00BFA6]">{predictions.accuracy}%</div>
                  <div>
                    <div className="text-sm text-white">Trefferquote {selectedSport}</div>
                    <div className="text-[10px] text-gray-500">{predictions.correct}/{predictions.total} richtig</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {(predictions.predictions || []).map((p, i) => (
                    <div key={i} className="p-3 bg-[#1E1E1E] rounded-xl border border-white/5">
                      <div className="flex justify-between items-start mb-1">
                        <div className="text-[10px] text-gray-500">{p.league} | {p.date}</div>
                        <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          p.is_correct ? 'bg-[#00BFA6]/20 text-[#00BFA6]' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {p.is_correct ? 'Richtig' : 'Falsch'}
                        </div>
                      </div>
                      <div className="text-sm text-white mb-2">{p.home_team} vs {p.away_team}</div>
                      <div className="flex gap-2 text-[10px]">
                        <span className="px-2 py-0.5 rounded bg-white/5 text-gray-400">Elo: {p.home_elo} vs {p.away_elo}</span>
                        <span className={`px-2 py-0.5 rounded ${
                          p.prediction === 'home' ? 'bg-blue-500/20 text-blue-400' :
                          p.prediction === 'away' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          KI: {p.prediction === 'home' ? 'Heim' : p.prediction === 'away' ? 'Ausw.' : 'X'} ({p.confidence}%)
                        </span>
                      </div>
                      {p.probabilities && (
                        <div className="flex gap-1 mt-2">
                          {[['Heim', p.probabilities.home], ['X', p.probabilities.draw], ['Ausw.', p.probabilities.away]].map(([label, prob], j) => (
                            <div key={j} className="flex-1 text-center">
                              <div className="h-1 bg-white/5 rounded-full mb-0.5">
                                <div className="h-1 rounded-full bg-[#7B1FA2]" style={{ width: `${prob}%` }} />
                              </div>
                              <span className="text-[9px] text-gray-500">{label} {prob}%</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">Keine Vorhersagen verfuegbar</div>
            )}
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default MultiSportPage;
